import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../api/store";
import { EligibilityAge, GCToGEAMoPInitialState } from "./gc-tog-ea-mop-state";
import { SubTypeOption } from "../../../api/gms/group-category/group-category-api-state";

const initialState: GCToGEAMoPInitialState = {
    //GC
    selectedGroupClassificationBox: null,
    otherSpecificationInputGC: null,
    // ToG
    selectedTypeOfGroupBox: null,
    otherSpecificationInputToG: null,
    selectedSubType: [],
    // EA
    eligibilityAge: [
        { label: "Minimum Age", age: null },
        { label: "Max Age", age: null },
    ],
    //MoP
    selectedFundingArrangements: null,
    othersNumberOfMonthsMoP: null
};

const gcToGEAMoPSlice = createSlice({
    name: "gcToGEAMoPSlice",
    initialState,
    reducers: {
        // GC
        setGroupClassification: (state, action: PayloadAction<SubTypeOption>) => {
            state.selectedGroupClassificationBox = action.payload;
            if (action.payload.id !== 5) {
                state.otherSpecificationInputGC = "";
            }
        },
        setOtherSpecification: (state, action: PayloadAction<string | null>) => {
            state.otherSpecificationInputGC = action.payload;
        },
        resetGroupClassification: (state) => {
            state.selectedGroupClassificationBox = null;
            state.otherSpecificationInputGC = null;
        },
        // ToG
        setTypeOfGroup: (state, action: PayloadAction<SubTypeOption>) => {
            state.selectedTypeOfGroupBox = action.payload;
            state.selectedSubType = [];
            if (action.payload.id !== 7) {
                state.otherSpecificationInputToG = "";
            }
        },
        setOtherSpecificationToG: (state, action: PayloadAction<string | null>) => {
            state.otherSpecificationInputToG = action.payload;
        },
        setSelectedSubType: (state, action: PayloadAction<SubTypeOption>) => {
            const exists = state.selectedSubType.find(
                (item) => item.id === action.payload.id
            );

            if (exists) {
                state.selectedSubType = state.selectedSubType.filter(
                    (item) => item.id !== action.payload.id
                );
            } else {
                state.selectedSubType.push(action.payload);
            }
        },
        setSelectedSubTypesFromAPI: (state, action: PayloadAction<SubTypeOption[]>) => {
            state.selectedSubType = action.payload;
        },
        resetTypeOfGroup: (state) => {
            state.selectedTypeOfGroupBox = null;
            state.otherSpecificationInputToG = null;
            state.selectedSubType = [];
        },
        // EA
        setAgeByLabel: ( state, action: PayloadAction<{ label: EligibilityAge["label"]; age: number | null }> ) => {
            const item = state.eligibilityAge.find(i => i.label === action.payload.label);
            if (item) {
                // Allow null (for empty input)
                if (action.payload.age === null) {
                    item.age = null;
                    return;
                }
                // Enforce max 3 digits
                const age = Math.floor(action.payload.age); // ensure integer
                if (age >= 0 && age <= 65) {
                    item.age = age;
                }
            }
        },
        resetAgeProfile: (state) => {
            state.eligibilityAge = [
                { label: "Minimum Age", age: null },
                { label: "Max Age", age: null },
            ]
        },
        // MoP
        setSelectedFundingArrangementsBox: (state, action: PayloadAction<SubTypeOption | null>) => {
            state.selectedFundingArrangements = action.payload;
        },
        setOthersNumberOfMonths: (state, action: PayloadAction<number | null>) => {
            state.othersNumberOfMonthsMoP = action.payload
        },
        resetFundingArrangements: (state) => {
            state.selectedFundingArrangements = null;
            state.othersNumberOfMonthsMoP = null;
        }
    },
});

export const {
    // GC
    setGroupClassification,
    setOtherSpecification,
    resetGroupClassification,
    // ToG
    setTypeOfGroup,
    setOtherSpecificationToG,
    setSelectedSubType,
    setSelectedSubTypesFromAPI,
    resetTypeOfGroup,
    // EA
    setAgeByLabel,
    resetAgeProfile,
    // MoP
    setSelectedFundingArrangementsBox, 
    setOthersNumberOfMonths,
    resetFundingArrangements 
} = gcToGEAMoPSlice.actions;

// GC
export const selectGroupClassificationState = (state: RootState) => state.gcToGEAMoPSlice;
export const selectSelectedGroupClassification = (state: RootState) => state.gcToGEAMoPSlice.selectedGroupClassificationBox;
export const selectOtherSpecificationGC = (state: RootState) => state.gcToGEAMoPSlice.otherSpecificationInputGC;
// ToG
export const selectSelectedTypeOfGroup = (state: RootState) => state.gcToGEAMoPSlice.selectedTypeOfGroupBox;
export const selectSelectedSubType = (state: RootState) => state.gcToGEAMoPSlice.selectedSubType;
export const selectOtherSpecificationToG = (state: RootState) => state.gcToGEAMoPSlice.otherSpecificationInputToG;
// EA
export const selectAgeProfile = (state: RootState) => state.gcToGEAMoPSlice.eligibilityAge;
export const selectMinAge = (state: RootState) => {
    const minItem = state.gcToGEAMoPSlice.eligibilityAge.find(i => i.label === "Minimum Age");
    return minItem?.age ?? null;
};
export const selectMaxAge = (state: RootState) => {
    const maxItem = state.gcToGEAMoPSlice.eligibilityAge.find(i => i.label === "Max Age");
    return maxItem?.age ?? null;
};
// MoP
export const selectFundingArrangementsState = (state: RootState) => state.gcToGEAMoPSlice.selectedFundingArrangements;

export const selectModeOfPaymentNumberOfMonths = (state: RootState) => state.gcToGEAMoPSlice.othersNumberOfMonthsMoP;

export default gcToGEAMoPSlice.reducer;